import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Loader2, Save, Shield, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useRole, useRoles } from "@/features/roles/hooks/useRoles";
import { MODULES, ACTIONS, type Module } from "@folio/shared/permissions";

const roleFormSchema = z.object({
  name: z
    .string()
    .min(1, "Nombre es requerido")
    .max(50, "Maximo 50 caracteres"),
  description: z.string().max(200, "Maximo 200 caracteres").optional(),
  permissions: z.array(z.string()),
});

type RoleFormValues = z.infer<typeof roleFormSchema>;

export function RoleFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id && id !== "nuevo";

  const { role, isLoading: isLoadingRole } = useRole(
    isEditing ? id : undefined,
  );
  const { createRole, updateRole } = useRoles();
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      name: "",
      description: "",
      permissions: [],
    },
  });

  // Load role data when editing
  useEffect(() => {
    if (role) {
      form.reset({
        name: role.name,
        description: role.description || "",
        permissions: role.permissions,
      });
    }
  }, [role, form]);

  const handleSubmit = async (values: RoleFormValues) => {
    setIsSaving(true);
    try {
      if (isEditing && id) {
        await updateRole(id, {
          name: role?.isSystem ? undefined : values.name,
          description: role?.isSystem ? undefined : values.description,
          permissions: values.permissions,
        });
        toast.success("Rol actualizado correctamente");
      } else {
        await createRole({
          name: values.name,
          description: values.description,
          permissions: values.permissions,
        });
        toast.success("Rol creado correctamente");
      }
      navigate("/configuracion/roles");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al guardar rol");
    } finally {
      setIsSaving(false);
    }
  };

  // Generate all possible permissions
  const allPermissions = Object.entries(MODULES).flatMap(
    ([moduleName, moduleConfig]) =>
      Object.keys(moduleConfig.submodules).flatMap((submodule) =>
        ACTIONS.map((action) => ({
          value: `${moduleName}:${submodule}:${action}`,
          module: moduleName as Module,
          moduleLabel: moduleConfig.label,
          submodule,
          submoduleLabel:
            (moduleConfig.submodules as Record<string, { label: string }>)[
              submodule
            ]?.label || submodule,
          action,
        })),
      ),
  );

  // Group permissions by module
  const permissionsByModule = allPermissions.reduce(
    (acc, perm) => {
      if (!acc[perm.module]) {
        acc[perm.module] = {
          label: perm.moduleLabel,
          permissions: [],
        };
      }
      acc[perm.module]!.permissions.push(perm);
      return acc;
    },
    {} as Record<string, { label: string; permissions: typeof allPermissions }>,
  );

  const selectedPermissions = form.watch("permissions");

  const togglePermission = (permission: string) => {
    const current = form.getValues("permissions");
    if (current.includes(permission)) {
      form.setValue(
        "permissions",
        current.filter((p) => p !== permission),
      );
    } else {
      form.setValue("permissions", [...current, permission]);
    }
  };

  const toggleModule = (moduleName: string, permissions: string[]) => {
    const current = form.getValues("permissions");
    const allSelected = permissions.every((p) => current.includes(p));

    if (allSelected) {
      // Remove all from this module
      form.setValue(
        "permissions",
        current.filter((p) => !permissions.includes(p)),
      );
    } else {
      // Add all from this module
      const newPerms = new Set([...current, ...permissions]);
      form.setValue("permissions", Array.from(newPerms));
    }
  };

  if (isEditing && isLoadingRole) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/configuracion/roles")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">
            {isEditing ? `Editar Rol: ${role?.name}` : "Nuevo Rol"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditing
              ? "Modifique los permisos de este rol"
              : "Configure un nuevo rol con sus permisos"}
          </p>
        </div>
        {role?.isSystem && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded ml-auto">
            <Lock className="h-3 w-3" />
            Rol del Sistema
          </span>
        )}
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Informacion Basica
              </CardTitle>
              <CardDescription>Nombre y descripcion del rol</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="ej: Supervisor"
                        {...field}
                        disabled={role?.isSystem}
                      />
                    </FormControl>
                    {role?.isSystem && (
                      <FormDescription>
                        Los roles del sistema no pueden ser renombrados
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descripcion</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe las responsabilidades de este rol..."
                        className="resize-none"
                        rows={3}
                        {...field}
                        disabled={role?.isSystem}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card>
            <CardHeader>
              <CardTitle>Permisos</CardTitle>
              <CardDescription>
                Seleccione los permisos que tendra este rol.
                {selectedPermissions.length > 0 && (
                  <span className="ml-2 text-primary">
                    ({selectedPermissions.length} seleccionados)
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(permissionsByModule).map(
                ([moduleName, { label, permissions }]) => {
                  const modulePermValues = permissions.map((p) => p.value);
                  const selectedInModule = modulePermValues.filter((p) =>
                    selectedPermissions.includes(p),
                  );
                  const allSelected =
                    selectedInModule.length === modulePermValues.length;
                  const someSelected =
                    selectedInModule.length > 0 && !allSelected;

                  // Group by submodule
                  const bySubmodule = permissions.reduce(
                    (acc, perm) => {
                      if (!acc[perm.submodule]) {
                        acc[perm.submodule] = {
                          label: perm.submoduleLabel,
                          permissions: [],
                        };
                      }
                      acc[perm.submodule]!.permissions.push(perm);
                      return acc;
                    },
                    {} as Record<
                      string,
                      { label: string; permissions: typeof permissions }
                    >,
                  );

                  return (
                    <div key={moduleName} className="space-y-3">
                      {/* Module Header */}
                      <div className="flex items-center gap-2 border-b pb-2">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={() =>
                            toggleModule(moduleName, modulePermValues)
                          }
                          className={
                            someSelected
                              ? "data-[state=checked]:bg-primary/50"
                              : ""
                          }
                        />
                        <span className="font-medium">{label}</span>
                        <span className="text-xs text-muted-foreground">
                          ({selectedInModule.length}/{modulePermValues.length})
                        </span>
                      </div>

                      {/* Submodules */}
                      <div className="grid gap-4 pl-6 md:grid-cols-2">
                        {Object.entries(bySubmodule).map(
                          ([
                            submoduleName,
                            { label: subLabel, permissions: subPerms },
                          ]) => (
                            <div key={submoduleName} className="space-y-2">
                              <div className="text-sm font-medium text-muted-foreground">
                                {subLabel}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {subPerms.map((perm) => {
                                  const isSelected =
                                    selectedPermissions.includes(perm.value);
                                  return (
                                    <button
                                      key={perm.value}
                                      type="button"
                                      onClick={() =>
                                        togglePermission(perm.value)
                                      }
                                      className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                                        isSelected
                                          ? "bg-primary text-primary-foreground border-primary"
                                          : "bg-background hover:bg-muted border-input"
                                      }`}
                                    >
                                      {perm.action}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  );
                },
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/configuracion/roles")}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Save className="mr-2 h-4 w-4" />
              {isEditing ? "Guardar Cambios" : "Crear Rol"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
